import clsx from 'clsx';
import { Shimmer } from '../Shimmer';
import { ProductCardLayout, ProductCardVariants } from './NewProductCard';
import cardStyles from './NewProductCard.module.scss';
import styles from './NewProductCardSkeleton.module.scss';

type Props = {
    className?: string;
    variant?: ProductCardVariants;
    layout?: ProductCardLayout;
};

// Every bar fills the box it sits in, so they all start from the same placement.
const Bar: React.FC<{ className?: string }> = ({ className }) => (
    <Shimmer className={clsx(styles.bar, className)} />
);

/*
 * Built from the card's own classes, so it follows the card at every screen size and in both
 * densities. Only the few things owned by other components are copied, in the stylesheet.
 */
export const NewProductCardSkeleton: React.FC<Props> = ({
    className,
    variant = 'default',
    layout = 'default',
}) => (
    <div
        aria-hidden
        className={clsx(cardStyles.card, cardStyles[variant], cardStyles[layout], className)}
    >
        <div className={clsx(cardStyles.media, styles.media, styles.block)}>
            <Bar />
        </div>

        <div className={cardStyles.content}>
            <div className={cardStyles.titles}>
                <div className={clsx(cardStyles.brand, styles.block)}>
                    <Bar className={clsx(styles.short, styles.textBar, styles.thick)} />
                </div>

                <div className={clsx(cardStyles.name, styles.block)}>
                    <Bar className={clsx(styles.long, styles.textBar, styles.thick)} />
                </div>
            </div>

            <div className={cardStyles.wishlistButton}>
                <div className={clsx(styles.heart, styles.round, styles.block)}>
                    <Bar />
                </div>
            </div>

            <div className={cardStyles.attributes}>
                <div className={clsx(styles.summaryLine, styles.block)}>
                    <Bar className={clsx(styles.medium, styles.textBar, styles.thick)} />
                </div>

                <div className={clsx(styles.variantsLine, styles.block)}>
                    <Bar className={clsx(styles.short, styles.textBar)} />
                </div>
            </div>

            <div className={clsx(cardStyles.prices, styles.priceBox, styles.block)}>
                <Bar
                    className={clsx(styles.short, styles.textBar, styles.thick, styles.firstLine)}
                />
                <Bar
                    className={clsx(styles.narrow, styles.textBar, styles.thick, styles.secondLine)}
                />
            </div>

            <div className={clsx(cardStyles.campaignText, styles.campaignLine, styles.block)}>
                <Bar className={clsx(styles.medium, styles.textBar, styles.firstLine)} />
            </div>

            <ul className={cardStyles.stockLabelList}>
                {[0, 1].map((row) => (
                    <li key={row} className={cardStyles.stockLabelItem}>
                        <div className={clsx(styles.dot, styles.round, styles.block)}>
                            <Bar />
                        </div>

                        <div className={clsx(styles.stockText, styles.block)}>
                            <Bar />
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    </div>
);
